import { useEffect, useState, memo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  slimmingLink,
  skincareLinks,
  haircareLinks,
  dentalLinks,
  aboutLinks,
} from "./MenuItems";

import {
  faBars,
  faPhone,
  faChevronDown,
  faChevronUp,
  faGlobe,
  faMobile,
  faNetworkWired,
  faPaintBrush,
  faTools,
  faCommentDots,
} from "@fortawesome/free-solid-svg-icons";
import NavbarLogo from "./navlogo.webp";

import { FaChevronDown } from "react-icons/fa";
import { MdArrowOutward } from "react-icons/md";

// Reusable DropdownMenu component - COMPACT VERSION
const DropdownMenu = memo(
  ({ isOpen, links, imageSrc, onMouseEnter, onMouseLeave, closeAllMenus }) => {
    if (!isOpen) return null;

    return (
      <div
        className="origin-top-right absolute mt-2 w-64 rounded-lg bg-white shadow-lg border border-gray-200 z-50"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div className="p-3">
          {links.map((link) => (
            <a
              href={link.path}
              key={link.path}
              className="block px-3 py-2 text-sm text-gray-700 hover:bg-secondary hover:text-primary rounded-md transition-colors"
            >
              <div className="flex items-center gap-3">
                <FontAwesomeIcon
                  className="text-primary w-4 h-4"
                  icon={link.icon}
                />
                <div className="flex-1">
                  <div className="font-medium">{link.title}</div>
                  {link.description && (
                    <p className="text-xs text-gray-500 mt-1">
                      {link.description}
                    </p>
                  )}
                </div>
              </div>
            </a>
          ))}
        </div>

        {imageSrc && (
          <div className="border-t border-gray-200 p-3 bg-secondary rounded-b-lg">
            <img
              className="w-full h-20 object-cover rounded-md mb-2"
              src={imageSrc}
              alt="Service Image"
            />
            <a
              href="/services"
              className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-medium"
            >
              View All Services
              <MdArrowOutward className="text-xs" />
            </a>
          </div>
        )}
      </div>
    );
  },
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [openMenus, setOpenMenus] = useState({
    slimming: false,
    skincare: false,
    haircare: false,
    dental: false,
    about: false,
    mobileMenu: false,
  });
  const [isOpen, setIsOpen] = useState(false); // For mobile menu
  const [openMobileSubmenus, setOpenMobileSubmenus] = useState({});
  const [closeTimeouts, setCloseTimeouts] = useState({}); // Per-menu timeouts

  const closeAllMenus = () => {
    setOpenMenus({
      slimming: false,
      skincare: false,
      haircare: false,
      dental: false,
      about: false,
      mobileMenu: false,
    });
  };

  const handleMouseEnter = (menu) => {
    if (closeTimeouts[menu]) {
      clearTimeout(closeTimeouts[menu]);
      setCloseTimeouts((prev) => ({ ...prev, [menu]: null }));
    }
    closeAllMenus();
    setOpenMenus((prev) => ({ ...prev, [menu]: true }));
  };

  const handleMouseLeave = (menu) => {
    const timeout = setTimeout(() => {
      setOpenMenus((prev) => ({ ...prev, [menu]: false }));
    }, 300); // Reduced timeout for faster response
    setCloseTimeouts((prev) => ({ ...prev, [menu]: timeout }));
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const menuConfigs = [
    {
      key: "slimming",
      title: "Slimming",
      href: "/slimming",
      links: slimmingLink,
      icon: faGlobe,
    },
    {
      key: "skincare",
      title: "Skin Care",
      href: "/skincare",
      links: skincareLinks,
      icon: faMobile,
    },
    {
      key: "haircare",
      title: "Hair Care",
      href: "/haircare",
      links: haircareLinks,
      icon: faNetworkWired,
    },
    {
      key: "dental",
      title: "Dental",
      href: "/dental",
      links: dentalLinks,
      icon: faPaintBrush,
    },
    {
      key: "about",
      title: "About",
      href: "/aboutus",
      links: aboutLinks,
      imageSrc: null,
      icon: faTools,
    },
    {
      key: "contactus",
      title: "Contact",
      href: "/contactus",
      links: [],
      icon: faCommentDots,
    },
  ];

  const toggleMobileSubmenu = (key) => {
    setOpenMobileSubmenus((prev) => {
      const isAlreadyOpen = !!prev[key];
      return {
        [key]: !isAlreadyOpen,
      };
    });
  };

  return (
    <section
      className={`sticky w-full top-0 z-50 drop-shadow-lg backdrop-blur-sm transition-colors duration-500 ${
        scrolled ? "bg-white/90" : "bg-white"
      }`}
    >
      {/* Main navbar */}
      <div className="px-4 sm:px-6 w-full h-20 flex justify-between items-center text-primary max-w-7xl mx-auto">
        {/* Phone - Mobile Only */}
        <div className="block sm:hidden">
          <a
            href="tel:+918977733002"
            className="flex items-center gap-2 text-primary"
          >
            <FontAwesomeIcon className="text-lg" icon={faPhone} />
          </a>
        </div>

        {/* Logo */}
        <a href="/" className="mx-auto sm:mx-0">
          <img
            className="p-2 h-auto w-[180px] lg:w-[220px]"
            src={NavbarLogo}
            alt="DermaSlim Wellness Logo"
            height="auto"
          />
        </a>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center space-x-1">
          {menuConfigs.map((menu) => (
            <div
              key={menu.key}
              className="relative"
              onMouseEnter={() => handleMouseEnter(menu.key)}
              onMouseLeave={() => handleMouseLeave(menu.key)}
            >
              {menu.links.length > 0 ? (
                <button className="flex items-center gap-1 px-4 py-3 text-[15px] font-medium text-gray-700 hover:text-primary transition-colors">
                  {menu.title}
                  <FaChevronDown className="text-xs mt-0.5" />
                </button>
              ) : (
                <a
                  href={menu.href}
                  className="px-4 py-3 text-[15px] font-medium text-gray-700 hover:text-primary transition-colors"
                >
                  {menu.title}
                </a>
              )}

              {/* Dropdown Menu */}
              {menu.links.length > 0 && (
                <DropdownMenu
                  isOpen={openMenus[menu.key]}
                  links={menu.links}
                  imageSrc={menu.imageSrc}
                  onMouseEnter={() => handleMouseEnter(menu.key)}
                  onMouseLeave={() => handleMouseLeave(menu.key)}
                  closeAllMenus={closeAllMenus}
                />
              )}
            </div>
          ))}

          {/* Book Now Button */}
          <div className="ml-2">
            <a
              href="/contactus"
              className="inline-flex items-center gap-2 bg-primary text-white px-5 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              Book Appointment
              <MdArrowOutward className="text-sm" />
            </a>
          </div>
        </div>

        {/* Mobile Menu Button */}
        <div className="lg:hidden">
          <button
            className="p-2 text-primary"
            onClick={() => setIsOpen((prev) => !prev)}
          >
            <FontAwesomeIcon icon={faBars} className="text-xl" />
          </button>

          {/* Mobile Menu */}
          {isOpen && (
            <div className="absolute top-full left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
              <div className="flex flex-col p-4">
                {menuConfigs.map((menu, i) => (
                  <div key={i}>
                    {menu.links.length > 0 ? (
                      <>
                        <button
                          onClick={() => toggleMobileSubmenu(menu.key)}
                          className="w-full flex justify-between items-center px-4 py-3 text-[15px] text-gray-700 hover:bg-secondary hover:text-primary transition rounded-md"
                        >
                          <div className="flex items-center gap-3">
                            <FontAwesomeIcon
                              icon={menu.icon}
                              className="text-[15px] text-primary"
                            />
                            {menu.title}
                          </div>
                          <FontAwesomeIcon
                            icon={
                              openMobileSubmenus[menu.key]
                                ? faChevronUp
                                : faChevronDown
                            }
                            className="text-xs text-primary"
                          />
                        </button>

                        {openMobileSubmenus[menu.key] && (
                          <div className="ml-8 border-l border-secondary pl-3 py-2 space-y-1">
                            {menu.links.map((sub, idx) => (
                              <a
                                key={idx}
                                href={sub.path}
                                onClick={() => setIsOpen(false)}
                                className="flex items-center gap-3 px-3 py-2 text-[14px] text-gray-600 hover:text-primary hover:bg-secondary rounded-md"
                              >
                                <FontAwesomeIcon
                                  icon={sub.icon}
                                  className="text-[13px] text-primary"
                                />
                                {sub.title}
                              </a>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <a
                        href={menu.href}
                        onClick={() => setIsOpen(false)}
                        className="w-full flex items-center gap-3 px-4 py-3 text-[15px] text-gray-700 hover:bg-secondary hover:text-primary transition rounded-md"
                      >
                        <FontAwesomeIcon
                          icon={menu.icon}
                          className="text-[15px] text-primary"
                        />
                        {menu.title}
                      </a>
                    )}
                  </div>
                ))}

                {/* Book Now in Mobile */}
                <a
                  href="/contactus"
                  className="mt-4 px-4 py-3 text-[15px] bg-primary text-white rounded-md text-center font-semibold hover:bg-primary/90 transition"
                  onClick={() => setIsOpen(false)}
                >
                  Book Appointment
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Navbar;
